// Executive Command Center (FS-CMD-1) — one-screen command center with views.
//
// View model (?view= searchParam):
//   - pulse (default): the command-center content restructured into three
//     zones before scroll: (1) attention strip + KPI band, (2) portfolio
//     posture (LOE band always visible + tabbed density panels), (3) the
//     decisions/briefings zone (leadership review queue + Risk Sentinel).
//   - analytics: the entire Mission Impact & Resource Alignment page body
//     (FS-CMD-2 merge), rendered from MissionImpactAnalytics. This view keeps
//     the NARROWER /analytics/mission-impact RBAC gate (ES/PfM) — other roles
//     get an access-explained state, never a silent 403.
//
// Filter honesty (AC-CMD-1.2): the portfolio filter re-scopes every zone that
// carries portfolio-dimension data (KPI band, attention strip, investment
// donut, gauges, scatter, objectives, review queue). Zones whose sources have
// no portfolio dimension (LOE rollups, lifecycle pipeline, operational
// posture payload, Risk Sentinel) label themselves "Enterprise scope" instead
// of silently ignoring the filter.
//
// Trust: real `asOf` from the data load; no fabricated deltas or fallback
// metrics; the mission gauge may be derived from real portfolio scores only
// and is labeled as derived.
import { useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { rbac } from '@dha-nexus/shared';
import { useCurrentUser } from '../../app/CurrentUserContext.jsx';
import { PageHeader } from '../../app/PageHeader.jsx';
import { useApi, useMutation } from '../../lib/useApi.js';
import { useIsMobileShell } from '../../lib/useIsMobileShell.js';
import { agentLabel, humanizeEnum } from '../../lib/fieldLabels.js';
import { api } from '../../lib/apiClient.js';
import { toast, activityRail } from '../../components/index.js';
import {
  PageActionStrip,
  ErrorState,
  EmptyState,
  KpiCard,
  GaugeCard,
  AlertCard,
  InvestmentDonut,
  MissionImpactScatter,
  Sparkline,
  StatusDonutMini,
  KpiBandSkeleton,
  ChartSkeleton,
  StaggerReveal,
} from '../../components/index.js';
import { MissionImpactAnalytics } from './MissionImpactAnalytics.jsx';
import { DirectorDailyBriefCard } from './DirectorDailyBriefCard.jsx';
import { ExplainChartButton } from '../../components/inspectors/ExplainChartButton.jsx';
import { buildDonutVisual, buildScatterVisual, buildHealthVisual } from './chartExplainContext.js';
import { buildExecutiveDeckPath } from '../briefings/reportEntryLinks.js';
import './executive.css';
import './executive.redesign.css';

const HEALTH_TONE = {
  green: 'sage',
  sage: 'sage',
  amber: 'amber',
  red: 'brick',
  brick: 'brick',
  blue: 'blue',
  unknown: 'slate',
};

const HEALTH_LABEL = {
  green: 'On track',
  sage: 'On track',
  on_track: 'On track',
  onTrack: 'On track',
  stable: 'Stable',
  amber: 'Watch',
  watch: 'Watch',
  at_risk: 'At risk',
  atRisk: 'At risk',
  red: 'Critical',
  brick: 'Critical',
  critical: 'Critical',
  blocked: 'Blocked',
  blue: 'Notice',
  unknown: 'Pending',
};

const MISSION_IMPACT_ROUTE = '/analytics/mission-impact';

const POSTURE_TABS = [
  { key: 'invest', label: 'Investment & mission' },
  { key: 'lifecycle', label: 'Lifecycle pipeline' },
  { key: 'ops', label: 'Operational posture' },
];

function formatHealth(raw, fallback) {
  if (!raw) return HEALTH_LABEL[fallback] || 'Pending';
  const key = String(raw).trim();
  if (HEALTH_LABEL[key]) return HEALTH_LABEL[key];
  const lower = key.toLowerCase();
  if (HEALTH_LABEL[lower]) return HEALTH_LABEL[lower];
  // graceful camelCase / snake_case to Title Case
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/^\w/, (c) => c.toUpperCase());
}

function bucketProject(p) {
  const score = p.healthScores?.overall;
  if (typeof score === 'number') {
    if (score >= 75) return 'green';
    if (score >= 60) return 'amber';
    return 'red';
  }
  const s = String(p.health?.overall || p.health || p.overallHealth || p.status || '').toLowerCase();
  if (/green|stable|on_?track/.test(s)) return 'green';
  if (/amber|watch|at_?risk/.test(s)) return 'amber';
  if (/red|critical|block/.test(s)) return 'red';
  return 'unknown';
}

// Read a real per-project trend when the server includes one on a critical
// project. We never fabricate: without a real series, the sparkline is omitted.
function realProjectSeries(project) {
  const values = project?.visuals?.sparklineSeries?.burn?.values;
  return Array.isArray(values) && values.length >= 2 ? values : null;
}

// Map an executive health bucket to a traffic-light tone for the rollup donut.
const BUCKET_DONUT_TONE = { green: 'sage', amber: 'amber', red: 'brick', unknown: 'slate' };

// "ROLE-EXECUTIVE-SPONSOR" -> "Executive Sponsor" for the access explainer.
function roleDisplayName(roleId) {
  return String(roleId)
    .replace(/^ROLE-/, '')
    .toLowerCase()
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// Real freshness only: renders nothing unless the load timestamp is a Date.
function formatAsOf(loadedAt) {
  if (!(loadedAt instanceof Date) || Number.isNaN(loadedAt.getTime())) return null;
  return `As of ${loadedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
}

// Phone treatment for the mission-impact-vs-spend scatter (locked chart
// decision 2 — applied consistently with /home). The scatter is not legible at
// phone width, so on mobile the Command Center shows a key-value summary of the
// SAME points (portfolio → mission impact · planned spend) with a "View chart"
// disclosure that renders the real scatter inside a contained .m-scroll frame.
// Desktop renders the scatter inline exactly as before, with drill-through.
function ExecScatterFitOrSummary({ points, isMobile, onSelect }) {
  if (!isMobile) {
    return <MissionImpactScatter data={points} height={320} onSelect={onSelect} />;
  }
  const top = [...points].sort((a, b) => Number(b.impact || 0) - Number(a.impact || 0)).slice(0, 6);
  return (
    <div data-testid="exec-scatter-mobile">
      <ul className="execx-scatter-summary" data-testid="exec-scatter-summary">
        {top.map((p) => (
          <li key={p.id || p.name}>
            <Link className="execx-scatter-name" to={`/portfolios/${p.id}`}>
              {p.name}
            </Link>
            <span className="execx-scatter-metric">
              impact {Math.round(Number(p.impact || 0))} · ${Number(p.spend || 0).toFixed(0)}M
            </span>
          </li>
        ))}
      </ul>
      <details className="execx-chart-disclosure">
        <summary data-testid="exec-scatter-disclosure">View chart</summary>
        <div className="m-scroll execx-scatter-scroll">
          <div className="execx-scatter-scroll__inner">
            <MissionImpactScatter data={points} height={280} onSelect={onSelect} />
          </div>
        </div>
      </details>
    </div>
  );
}

function CommandCenterViewSwitch({ view, onSelect }) {
  return (
    <div className="execx-view-switch" role="tablist" aria-label="Command Center views">
      <button
        type="button"
        role="tab"
        aria-selected={view === 'pulse'}
        className={`btn ${view === 'pulse' ? 'primary' : 'ghost'} sm`}
        onClick={() => onSelect('pulse')}
      >
        Pulse
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={view === 'analytics'}
        className={`btn ${view === 'analytics' ? 'primary' : 'ghost'} sm`}
        onClick={() => onSelect('analytics')}
      >
        Analytics
      </button>
    </div>
  );
}

// Access-explained state for the Analytics view (never a silent 403). The
// required roles are read from the shared RBAC route map so this stays in
// lockstep with the /analytics/mission-impact gate.
function AnalyticsAccessNotice({ onBackToPulse }) {
  const requiredRoles = (rbac.ROUTE_PERMISSIONS?.[MISSION_IMPACT_ROUTE] || []).map(roleDisplayName);
  const roleSentence = requiredRoles.length ? requiredRoles.join(' and ') : 'a portfolio-analytics';
  return (
    <section
      className="card execx-access-note"
      data-testid="analytics-access-note"
      aria-label="Analytics view access"
    >
      <div className="eyebrow">Analytics view</div>
      <h2 className="card-title">Mission-impact analytics is limited to {roleSentence} roles</h2>
      <p className="muted">
        The Analytics view carries the mission-impact rollup — ROI and outcome gauges, predictive
        slippage, the redundancy heatmap, and the AI reallocation advisor — which keeps the narrower
        access policy of the mission-impact analytics route. Your current role can use every other
        Command Center zone in the Pulse view. Ask your administrator for{' '}
        {requiredRoles.length ? requiredRoles.join(' or ') : 'analytics'} access if you need this
        analysis.
      </p>
      <button type="button" className="btn ghost sm" onClick={onBackToPulse}>
        Back to Pulse
      </button>
    </section>
  );
}

export function ExecutiveCommandCenterPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { roles } = useCurrentUser();
  const isMobileShell = useIsMobileShell();
  const [filterPortfolio, setFilterPortfolio] = useState('');
  const [postureTab, setPostureTab] = useState('invest');
  const summary = useApi('/analytics/executive-summary', {
    query: filterPortfolio ? { portfolioId: filterPortfolio } : undefined,
  });
  const alerts = useApi('/analytics/predictive-alerts');
  const portfolios = useApi('/portfolios', { query: { limit: 50 } });
  const loes = useApi('/loes');
  const draftBrief = useMutation();
  const saveView = useMutation();
  // Lets the "Draft AI narrative" action surface a fresh governed narrative in
  // the Director's Daily Brief card instead of dead-ending in a toast.
  const briefCardRef = useRef(null);

  const view = searchParams.get('view') === 'analytics' ? 'analytics' : 'pulse';
  function selectView(next) {
    const params = new URLSearchParams(searchParams);
    if (next === 'analytics') params.set('view', 'analytics');
    else params.delete('view');
    setSearchParams(params);
  }

  const data = summary.data?.data;
  const visuals = data?.visuals || {};
  const operationalPosture = data?.operationalPosture || {};
  const alertList = alerts.data?.data?.alerts || [];
  const portfolioList = useMemo(() => portfolios.data?.data ?? [], [portfolios.data?.data]);
  const loeList = useMemo(() => loes.data?.data ?? [], [loes.data?.data]);
  const summaryPortfolios = useMemo(() => data?.portfolios ?? [], [data?.portfolios]);
  const criticalProjects = useMemo(() => data?.criticalProjects ?? [], [data?.criticalProjects]);
  const canOpenMissionImpact = rbac.canSeeRoute(MISSION_IMPACT_ROUTE, roles);

  const filterName = filterPortfolio
    ? portfolioList.find((portfolio) => portfolio.id === filterPortfolio)?.shortName ||
      portfolioList.find((portfolio) => portfolio.id === filterPortfolio)?.name ||
      filterPortfolio
    : null;
  const scopeLabel = filterName ? `Scoped to ${filterName}` : 'All portfolios';
  const enterpriseScopeLabel = filterPortfolio
    ? 'Enterprise scope · portfolio filter not applied'
    : 'Enterprise scope';

  const healthBuckets = data?.projectsByHealth || { green: 0, amber: 0, red: 0, unknown: 0 };
  const totalProjects = data?.counts?.projects || 1;
  // Mission average derives ONLY from portfolios that actually report a
  // mission-impact score — missing scores are excluded, never zero-filled or
  // defaulted. When no portfolio reports one, the derived gauge is omitted.
  const missionScores = summaryPortfolios
    .map((portfolio) => portfolio.missionImpact)
    .filter((score) => typeof score === 'number' && !Number.isNaN(score));
  const avgMission = missionScores.length
    ? Math.round(missionScores.reduce((sum, score) => sum + score, 0) / missionScores.length)
    : null;
  const totalPlanned = Number(operationalPosture.budgetExecution?.plannedBudgetUsdM || 0);
  const totalForecastVariance = Number(
    operationalPosture.budgetExecution?.forecastVarianceUsdM || 0,
  );

  const investmentSlices = useMemo(() => {
    if (visuals.investmentDonut?.slices?.length) {
      return visuals.investmentDonut.slices;
    }
    return summaryPortfolios
      .map((p) => ({
        id: p.id,
        name: p.name,
        value: p.plannedBudgetUsdM || 0,
      }))
      .filter((s) => s.value > 0);
  }, [summaryPortfolios, visuals.investmentDonut?.slices]);

  const scatter = useMemo(() => {
    if (visuals.missionImpactScatter?.points?.length) {
      return visuals.missionImpactScatter.points;
    }
    // Fallback plots ONLY portfolios with a real mission score — no invented
    // impact=70 points. Portfolios without scoring simply do not appear.
    return summaryPortfolios
      .map((p) => {
        const impact = p.missionImpact;
        if (typeof impact !== 'number' || Number.isNaN(impact)) return null;
        return {
          id: p.id,
          name: p.name,
          spend: p.plannedBudgetUsdM || 0,
          impact,
          size: (p.activeProjects || 1) * 80,
          healthTone: HEALTH_TONE[bucketProject(p)] || 'blue',
        };
      })
      .filter(Boolean);
  }, [summaryPortfolios, visuals.missionImpactScatter?.points]);

  // Prefer the real per-KPI series the executive-summary payload returns. The
  // endpoint scopes these cards server-side whenever a portfolio is selected.
  // The fallback uses server counts/buckets only and never fabricates a trend.
  const serverKpiCards = visuals.kpiCards?.length ? visuals.kpiCards : null;
  const executiveKpis = serverKpiCards || [
    {
      id: 'executive-portfolios',
      label: 'Portfolios',
      value: data?.counts?.portfolios || 0,
      footer: `${data?.counts?.projects || 0} projects in flight · ${scopeLabel}`,
      sparklineTone: 'blue',
    },
    {
      id: 'executive-on-track',
      label: 'On track',
      value: healthBuckets.green,
      delta: Math.round((healthBuckets.green / totalProjects) * 100),
      deltaLabel: `${Math.round((healthBuckets.green / totalProjects) * 100)}% green`,
      deltaTone: 'sage',
      sparklineTone: 'sage',
      status: 'sage',
      statusLabel: 'Stable',
      footer: scopeLabel,
    },
    {
      id: 'executive-at-risk',
      label: 'At risk',
      value: healthBuckets.amber,
      delta: Math.round((healthBuckets.amber / totalProjects) * 100),
      deltaLabel: `${Math.round((healthBuckets.amber / totalProjects) * 100)}% amber`,
      deltaTone: 'amber',
      sparklineTone: 'amber',
      status: 'amber',
      statusLabel: 'Watch',
      footer: scopeLabel,
    },
    {
      id: 'executive-critical',
      label: 'Critical',
      value: healthBuckets.red,
      delta: Math.round((healthBuckets.red / totalProjects) * 100),
      deltaLabel: `${Math.round((healthBuckets.red / totalProjects) * 100)}% red`,
      deltaTone: 'brick',
      sparklineTone: 'brick',
      status: 'brick',
      statusLabel: 'Critical',
      footer: scopeLabel,
    },
  ];

  const payloadGauges = visuals.gauges;
  const derivedMissionSublabel = `Derived · mean of ${missionScores.length} portfolio mission-impact score${
    missionScores.length === 1 ? '' : 's'
  }${filterName ? ` · scoped to ${filterName}` : ''}`;

  // CSS-only rollup ring over the live health buckets (sage/amber/brick),
  // bound to executive-traffic-light tones.
  const healthDonutSegments = [
    { label: 'On track', count: healthBuckets.green, tone: BUCKET_DONUT_TONE.green },
    { label: 'At risk', count: healthBuckets.amber, tone: BUCKET_DONUT_TONE.amber },
    { label: 'Critical', count: healthBuckets.red, tone: BUCKET_DONUT_TONE.red },
    { label: 'Pending', count: healthBuckets.unknown, tone: BUCKET_DONUT_TONE.unknown },
  ];

  const okrs = useMemo(() => {
    // Progress is the portfolio's real mission-impact score or nothing — rows
    // without a reported score render "—" instead of a fabricated 60.
    return summaryPortfolios.slice(0, 3).map((p) => {
      const score = p.missionImpact;
      return {
        id: p.id,
        label: p.name,
        progress: typeof score === 'number' && !Number.isNaN(score) ? score : null,
        status: bucketProject(p),
      };
    });
  }, [summaryPortfolios]);

  const lifecycleCounts = useMemo(
    () =>
      loeList.reduce(
        (acc, loe) => {
          acc.proposed += Number(loe.proposed || 0);
          acc.current += Number(loe.current || 0);
          acc.completed += Number(loe.completed || 0);
          acc.denied += Number(loe.denied || 0);
          acc.deferred += Number(loe.deferred || 0);
          return acc;
        },
        { proposed: 0, current: 0, completed: 0, denied: 0, deferred: 0 },
      ),
    [loeList],
  );

  const lifecycleCards = [
    {
      key: 'proposed',
      label: 'Proposed requests',
      value: lifecycleCounts.proposed,
      href: '/loe-dashboard?bucket=proposed',
      detail: 'Requests awaiting director prioritization.',
    },
    {
      key: 'current',
      label: 'Current projects',
      value: lifecycleCounts.current,
      href: '/loe-dashboard?bucket=current',
      detail: 'Active projects aligned to current LOE demand.',
    },
    {
      key: 'completed',
      label: 'Completed projects',
      value: lifecycleCounts.completed,
      href: '/loe-dashboard?bucket=completed',
      detail: 'Delivered work that can feed closeout and archive decisions.',
    },
    {
      key: 'denied',
      label: 'Denied requests',
      value: lifecycleCounts.denied,
      href: '/loe-dashboard?bucket=denied',
      detail: 'Requests declined after portfolio review.',
    },
    {
      key: 'deferred',
      label: 'Deferred requests',
      value: lifecycleCounts.deferred,
      href: '/loe-dashboard?bucket=deferred',
      detail: 'Requests held or routed for follow-up.',
    },
  ];

  const loeAlignmentRows = useMemo(
    () =>
      loeList.slice().sort((left, right) => {
        const rightPriority =
          Number(right.proposed || 0) * 3 +
          Number(right.current || 0) * 2 +
          Number(right.overdueActions || 0);
        const leftPriority =
          Number(left.proposed || 0) * 3 +
          Number(left.current || 0) * 2 +
          Number(left.overdueActions || 0);
        return (
          rightPriority - leftPriority ||
          String(left.loe || '').localeCompare(String(right.loe || ''))
        );
      }),
    [loeList],
  );

  const resourceAllocationSummary = operationalPosture.resourceAllocation || {
    activeAllocationCount: 0,
    loeCount: loeList.length,
    totalHoursPerWeek: 0,
  };
  const riskPostureSummary = operationalPosture.riskPosture || {
    openRiskCount: 0,
    elevatedRiskCount: 0,
  };
  const budgetExecutionSummary = operationalPosture.budgetExecution || {
    plannedBudgetUsdM: 0,
    forecastVarianceUsdM: 0,
    portfolioCount: data?.counts?.portfolios || 0,
  };

  // Attention strip — the top signals an executive reads first. Every count is
  // computed from the same scoped data the zones render, with an explicit
  // scope note per signal (alerts have no portfolio dimension and say so).
  const needsReview = healthBuckets.amber + healthBuckets.red;
  const attentionSignals = [
    {
      id: 'attention-projects',
      tone: healthBuckets.red > 0 ? 'brick' : needsReview > 0 ? 'amber' : 'sage',
      title:
        needsReview > 0
          ? `${needsReview} project${needsReview === 1 ? '' : 's'} need${needsReview === 1 ? 's' : ''} leadership attention`
          : 'All projects on track',
      detail: `${healthBuckets.red} critical · ${healthBuckets.amber} at risk · ${scopeLabel}`,
      href: '/projects',
      cta: 'Open projects',
    },
    {
      id: 'attention-alerts',
      tone: alertList.length > 0 ? 'amber' : 'sage',
      title: `${alertList.length} predictive alert${alertList.length === 1 ? '' : 's'} awaiting review`,
      detail: `Risk Sentinel · ${enterpriseScopeLabel}`,
      href: '/ai/orchestration',
      cta: 'Open AI runs',
    },
    {
      id: 'attention-variance',
      tone: totalForecastVariance > 0 ? 'amber' : 'sage',
      title: `${totalForecastVariance >= 0 ? '+' : '−'}$${Math.abs(totalForecastVariance).toFixed(1)}M forecast variance`,
      detail: `Across ${data?.counts?.portfolios || 0} portfolio${data?.counts?.portfolios === 1 ? '' : 's'} · ${scopeLabel}`,
      href: '/financials',
      cta: 'Open financials',
    },
  ];
  const asOfLabel = formatAsOf(summary.loadedAt);

  // Feature H — the primary action is the governed deck factory (the real
  // human-review-and-export path that produces the Director deck), scoped to the
  // active portfolio filter when one is set, else enterprise/ENT-OWHA.
  function generateExecutiveDeck() {
    const deckPath = filterPortfolio
      ? buildExecutiveDeckPath({ scopeType: 'portfolio', scopeId: filterPortfolio })
      : buildExecutiveDeckPath();
    navigate(deckPath);
  }

  // Secondary action: one click, one governed run — drive the Director's Daily
  // Brief card refresh (POST /ai/daily-brief) rather than also drafting a
  // second legacy briefing run in parallel.
  async function draftNarrative() {
    await draftBrief.run(async () => {
      await briefCardRef.current?.refresh();
      return {};
    });
  }

  async function saveCurrentView() {
    try {
      const name = `Executive · ${filterPortfolio || 'all portfolios'} · ${new Date().toLocaleDateString()}`;
      await saveView.run(() =>
        api.post('/saved-views', {
          name,
          routePath: '/executive',
          scope: 'self',
          filters: { portfolioId: filterPortfolio || null, view },
        }),
      );
      toast.success({
        title: 'View saved',
        body: name,
        action: { label: 'Open saved views', onClick: () => navigate('/saved-views') },
      });
      activityRail.push({
        action: 'saved_view.create',
        targetType: 'saved_view',
        targetId: name,
        actorUserId: 'you',
      });
    } catch (e) {
      toast.error({ title: 'Could not save view', body: e.message });
    }
  }

  const switcher = <CommandCenterViewSwitch view={view} onSelect={selectView} />;

  // Analytics view (FS-CMD-2 merge). It fetches and renders its own data; the
  // narrower ES/PfM gate from /analytics/mission-impact stays enforced here.
  if (view === 'analytics') {
    return (
      <>
        <PageHeader />
        {switcher}
        {canOpenMissionImpact ? (
          <MissionImpactAnalytics />
        ) : (
          <AnalyticsAccessNotice onBackToPulse={() => selectView('pulse')} />
        )}
      </>
    );
  }

  const summaryScopePending = Boolean(
    data &&
      ((filterPortfolio && data.portfolioId !== filterPortfolio) ||
        (!filterPortfolio && data.portfolioId !== undefined)),
  );
  if (summary.loading || summaryScopePending || portfolios.loading || loes.loading)
    return (
      <div className="execx-loading">
        <KpiBandSkeleton count={4} />
        <div className="bento" style={{ marginTop: 'var(--space-5)' }}>
          <div className="span-8 card">
            <ChartSkeleton height={320} />
          </div>
          <div className="span-4 card">
            <ChartSkeleton height={320} />
          </div>
        </div>
      </div>
    );
  if (summary.error || loes.error) return <ErrorState error={summary.error || loes.error} />;
  if (!data)
    return (
      <EmptyState
        title="No executive data"
        description="This launch workspace has no portfolio data yet. Empty-state surfaces stay available while the database remains intentionally clean."
      />
    );

  return (
    <>
      {/* PRIME-SHELL-UX-001: single body page identity (formerly the topbar
          title), with the relocated About control beside it. */}
      <PageHeader />
      {switcher}
      <PageActionStrip
        filters={
          <select
            className="exec-filter"
            value={filterPortfolio}
            onChange={(e) => setFilterPortfolio(e.target.value)}
            aria-label="Portfolio filter"
          >
            <option value="">All portfolios</option>
            {portfolioList.map((p) => (
              <option key={p.id} value={p.id}>
                {p.shortName || p.name}
              </option>
            ))}
          </select>
        }
        actions={
          <>
            <button className="btn ghost" onClick={saveCurrentView} disabled={saveView.running}>
              Save view
            </button>
            <button className="btn ghost" onClick={draftNarrative} disabled={draftBrief.running}>
              {draftBrief.running ? 'Drafting…' : 'Draft AI narrative'}
            </button>
            <button className="btn primary tactile-press" onClick={generateExecutiveDeck}>
              Generate executive deck
            </button>
          </>
        }
      />

      <DirectorDailyBriefCard ref={briefCardRef} scopeId={filterPortfolio} />

      {/* ── Zone 1 · Attention & KPIs ─────────────────────────────────── */}
      <section className="execx-zone" aria-label="Attention and KPI zone" data-tour="exec-command-center">
        <div className="card execx-attention">
          <header className="execx-attention__head">
            <div>
              <div className="eyebrow">Needs leadership review</div>
              <h2 className="card-title">
                {attentionSignals.length} signal{attentionSignals.length === 1 ? '' : 's'}
              </h2>
            </div>
            <div className="row execx-attention__meta">
              <span className="chip outline sm">{scopeLabel}</span>
              {asOfLabel ? (
                <span className="chip slate sm" data-testid="exec-as-of">
                  {asOfLabel}
                </span>
              ) : null}
            </div>
          </header>
          <ul className="execx-attention__list">
            {attentionSignals.map((signal) => (
              <li key={signal.id} className="execx-attention__row">
                <span className={`chip ${signal.tone} sm execx-attention__sev`} aria-hidden>
                  <span className="chip-dot" />
                </span>
                <span className="execx-attention__text">
                  <strong>{signal.title}</strong>{' '}
                  <span className="muted small">{signal.detail}</span>
                </span>
                <Link className="btn link sm" to={signal.href}>
                  {signal.cta} →
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div className="bento exec-bento execx-kpi-band">
          {executiveKpis.slice(0, 4).map((kpi) => (
            <div className="span-3" key={kpi.id || kpi.label}>
              <KpiCard
                id={kpi.id}
                surface="executive"
                label={kpi.label}
                value={kpi.value}
                delta={kpi.delta}
                deltaLabel={kpi.deltaLabel}
                deltaTone={kpi.deltaTone}
                sparkline={kpi.series}
                sparklineTone={kpi.sparklineTone}
                status={kpi.status}
                statusLabel={kpi.statusLabel}
                footer={kpi.footer}
                richTooltip={kpi.richTooltip}
              />
            </div>
          ))}
        </div>
      </section>

      {/* ── Zone 2 · Portfolio posture ────────────────────────────────── */}
      <section className="execx-zone" aria-label="Portfolio posture zone">
        <div className="card exec-loe-alignment">
          <header className="exec-card-head">
            <div>
              <div className="eyebrow">Director priorities</div>
              <h2 className="card-title">Director LOE alignment</h2>
            </div>
            <div className="row execx-zone-scope">
              <span className="chip outline sm">{enterpriseScopeLabel}</span>
              <Link className="btn link" to="/loe-dashboard">
                All LOEs →
              </Link>
            </div>
          </header>
          {loeAlignmentRows.length === 0 ? (
            <EmptyState
              title="No LOE posture available"
              description="LOE-aligned intake and project records will appear here."
            />
          ) : (
            <div className="exec-loe-list">
              {loeAlignmentRows.map((loe) => {
                const loeHref = `/analytics/loes/${encodeURIComponent(loe.loe)}`;
                return (
                  <article key={loe.loe} className="exec-loe-row">
                    <div className="exec-loe-row__summary">
                      <Link className="exec-loe-row__title" to={loeHref}>
                        {loe.loe}
                      </Link>
                      <div className="muted small">
                        {Number(loe.activeProjects || 0)} active project
                        {Number(loe.activeProjects || 0) === 1 ? '' : 's'} ·{' '}
                        {Number(loe.openActions || 0)} open action
                        {Number(loe.openActions || 0) === 1 ? '' : 's'}
                      </div>
                    </div>
                    <div className="exec-loe-row__metrics">
                      <Link className="exec-loe-chip" to={`${loeHref}?bucket=proposed`}>
                        Open {loe.loe} proposed requests
                      </Link>
                      <Link className="exec-loe-chip" to={loeHref}>
                        {Number(loe.proposed || 0)} proposed requests
                      </Link>
                      {/* Guide-10 external UAT FINDING-010: the current-project
                          count deep-links to the Delivery drilldown it names —
                          the LOE workspace defaults to Prioritize otherwise. */}
                      <Link className="exec-loe-chip" to={`${loeHref}?tab=delivery`}>
                        {Number(loe.current || 0)} current projects
                      </Link>
                      <span className="exec-loe-chip muted">
                        {Number(loe.completed || 0)} completed projects
                      </span>
                      <span className="exec-loe-chip muted">
                        {Number(loe.denied || 0) + Number(loe.deferred || 0)} denied/deferred
                        projects
                      </span>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>

        <div
          className="row execx-posture-tabs"
          role="tablist"
          aria-label="Portfolio posture panels"
        >
          {POSTURE_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={postureTab === tab.key}
              className={`btn ${postureTab === tab.key ? 'primary' : 'ghost'} sm`}
              onClick={() => setPostureTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {postureTab === 'invest' ? (
          <div className="bento exec-bento" role="tabpanel" aria-label="Investment and mission">
            <div className="span-8 card flush exec-allocation">
              <header className="exec-card-head">
                <div>
                  <div className="eyebrow">Investment by portfolio</div>
                  <h2 className="card-title">${totalPlanned.toFixed(1)} M planned · FY26</h2>
                  <p className="muted">
                    Forecast variance · {totalForecastVariance >= 0 ? '+' : ''}
                    {totalForecastVariance.toFixed(1)} M
                  </p>
                </div>
                <div className="row execx-zone-scope">
                  <span className="chip outline sm">{scopeLabel}</span>
                  <ExplainChartButton
                    question="Explain the investment-by-portfolio donut I'm looking at: where is the money concentrated and what should I watch?"
                    buildVisual={() => buildDonutVisual(investmentSlices, { note: scopeLabel })}
                  />
                  <Link className="btn ghost sm" to="/financials">
                    Open financials →
                  </Link>
                </div>
              </header>
              {investmentSlices.length === 0 ? (
                <EmptyState title="No financial allocation" />
              ) : (
                <div className="exec-allocation-body">
                  <InvestmentDonut
                    data={investmentSlices}
                    centerValue={`$${totalPlanned.toFixed(0)}M`}
                    centerLabel="Total planned"
                    height={320}
                  />
                </div>
              )}
            </div>
            {/* Gauges render ONLY real payload metrics (or, for mission outcome, a
                derivation from real portfolio scores labeled as derived). When the
                payload does not provide a metric the gauge is omitted — no
                fabricated ROI, no invented "+12% vs FY25" delta. Payload gauges
                are enterprise aggregates and yield to the scoped derivation
                while a portfolio filter is active. */}
            {payloadGauges?.aggregateRoi?.value != null ||
            payloadGauges?.missionOutcome?.value != null ||
            avgMission != null ? (
              <div className="span-4 stack exec-gauge-rail">
                {payloadGauges?.aggregateRoi?.value != null ? (
                  <GaugeCard
                    id={payloadGauges.aggregateRoi.id || 'aggregate-roi'}
                    value={payloadGauges.aggregateRoi.value}
                    max={4}
                    unit={payloadGauges.aggregateRoi.unit || 'x'}
                    label={payloadGauges.aggregateRoi.label || 'Aggregate ROI'}
                    sublabel={payloadGauges.aggregateRoi.sublabel || 'Mission value / spend'}
                    tone={payloadGauges.aggregateRoi.tone || 'sage'}
                    deltaLabel={payloadGauges.aggregateRoi.deltaLabel}
                    deltaTone={payloadGauges.aggregateRoi.deltaTone || 'sage'}
                    richTooltip={payloadGauges.aggregateRoi.richTooltip}
                  />
                ) : null}
                {payloadGauges?.missionOutcome?.value != null ? (
                  <GaugeCard
                    id={payloadGauges.missionOutcome.id || 'mission-outcome'}
                    value={payloadGauges.missionOutcome.value}
                    label={payloadGauges.missionOutcome.label || 'Mission outcome score'}
                    sublabel={payloadGauges.missionOutcome.sublabel}
                    tone={payloadGauges.missionOutcome.tone || 'blue'}
                    deltaLabel={payloadGauges.missionOutcome.deltaLabel}
                    deltaTone={payloadGauges.missionOutcome.deltaTone || 'blue'}
                    richTooltip={payloadGauges.missionOutcome.richTooltip}
                  />
                ) : avgMission != null ? (
                  <GaugeCard
                    id="mission-outcome"
                    value={avgMission}
                    label="Mission outcome score"
                    sublabel={derivedMissionSublabel}
                    tone="blue"
                  />
                ) : null}
              </div>
            ) : null}

            <div className="span-7 card flush exec-scatter">
              <header className="exec-card-head">
                <div>
                  <div className="eyebrow">Mission impact vs spend</div>
                  <h2 className="card-title">Where the dollars create value</h2>
                </div>
                <div className="row execx-zone-scope">
                  <span className="chip outline sm">{scopeLabel}</span>
                  <ExplainChartButton
                    question="Explain the mission-impact vs spend scatter I'm looking at: which portfolios are the high-impact or high-spend outliers?"
                    buildVisual={() => buildScatterVisual(scatter, { note: scopeLabel })}
                  />
                  {canOpenMissionImpact ? (
                    <Link className="btn link" to="/executive?view=analytics">
                      Mission impact analytics →
                    </Link>
                  ) : null}
                </div>
              </header>
              {scatter.length === 0 ? (
                <EmptyState title="No portfolios with mission scoring" />
              ) : (
                <ExecScatterFitOrSummary
                  points={scatter}
                  isMobile={isMobileShell}
                  onSelect={(p) => navigate(`/portfolios/${p.id}`)}
                />
              )}
            </div>

            <div className="span-5 card exec-okrs">
              <header className="exec-card-head">
                <div>
                  <div className="eyebrow">Strategic alignment</div>
                  <h2 className="card-title">Mission objectives this fiscal</h2>
                </div>
                <span className="chip outline sm">{scopeLabel}</span>
              </header>
              <ul className="exec-okr-list">
                {okrs.map((k) => (
                  <li key={k.id} className={`exec-okr-row tone-${HEALTH_TONE[k.status]}`}>
                    <div className="row spread" style={{ marginBottom: 4 }}>
                      <strong>{k.label}</strong>
                      <span className="mono small">
                        {k.progress != null ? `${Math.round(k.progress)}%` : '—'}
                      </span>
                    </div>
                    {k.progress != null ? (
                      <div className="score-bar">
                        <i
                          className={HEALTH_TONE[k.status]}
                          style={{ width: `${Math.min(100, Math.round(k.progress))}%` }}
                        />
                      </div>
                    ) : (
                      <div className="muted small">Mission score not reported</div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : postureTab === 'lifecycle' ? (
          <div className="bento exec-bento" role="tabpanel" aria-label="Lifecycle pipeline">
            <div className="span-12 card exec-lifecycle execx-donut-card">
              <header className="exec-card-head">
                <div>
                  <div className="eyebrow">Scenario A opening</div>
                  <h2 className="card-title">Project lifecycle posture</h2>
                  <p className="muted">
                    Director-ready counts across proposed, current, completed, denied, and deferred
                    work.
                  </p>
                </div>
                <div className="row execx-zone-scope">
                  <span className="chip outline sm">{enterpriseScopeLabel}</span>
                  <Link className="btn ghost sm" to="/loe-dashboard">
                    Open LOE dashboard →
                  </Link>
                </div>
              </header>
              <StaggerReveal className="execx-lifecycle-grid">
                {lifecycleCards.map((card) => (
                  <Link
                    key={card.key}
                    className="execx-lifecycle-link"
                    to={card.href}
                    aria-label={`${card.value} ${card.label.toLowerCase()}`}
                  >
                    <KpiCard
                      surface="exec-lifecycle"
                      id={card.key}
                      label={card.label}
                      value={card.value}
                      footer={card.detail}
                    />
                  </Link>
                ))}
              </StaggerReveal>
            </div>
          </div>
        ) : (
          <div className="bento exec-bento" role="tabpanel" aria-label="Operational posture">
            <div className="span-12 card exec-operational-posture">
              <header className="exec-card-head">
                <div>
                  <div className="eyebrow">Cross-portfolio posture</div>
                  <h2 className="card-title">Operational posture</h2>
                </div>
                <span className="chip outline sm">{scopeLabel}</span>
              </header>
              <StaggerReveal className="execx-posture-grid">
                <KpiCard
                  surface="exec-posture"
                  id="resource-allocation"
                  label="Resource allocation"
                  value={resourceAllocationSummary.activeAllocationCount}
                  footer={`${resourceAllocationSummary.activeAllocationCount} active allocations across ${resourceAllocationSummary.loeCount || 0} LOEs.`}
                />
                <KpiCard
                  surface="exec-posture"
                  id="risk-posture"
                  label="Risk posture"
                  value={riskPostureSummary.openRiskCount}
                  status={riskPostureSummary.elevatedRiskCount > 0 ? 'amber' : 'sage'}
                  footer={`${riskPostureSummary.openRiskCount} open risks · ${riskPostureSummary.elevatedRiskCount || 0} elevated.`}
                />
                <KpiCard
                  surface="exec-posture"
                  id="budget-execution"
                  label="Budget execution"
                  value={`$${Number(budgetExecutionSummary.plannedBudgetUsdM || 0).toFixed(1)}M`}
                  footer={`$${Number(budgetExecutionSummary.plannedBudgetUsdM || 0).toFixed(1)}M planned · ${
                    Number(budgetExecutionSummary.forecastVarianceUsdM || 0) >= 0 ? '+' : ''
                  }${Number(budgetExecutionSummary.forecastVarianceUsdM || 0).toFixed(1)}M variance.`}
                />
              </StaggerReveal>
            </div>
          </div>
        )}
      </section>

      {/* ── Zone 3 · Decisions & briefings ────────────────────────────── */}
      <section className="execx-zone" aria-label="Decisions and briefings zone">
        <div className="bento exec-bento">
          <div className="span-8 card flush exec-critical execx-donut-card">
            <header className="exec-card-head">
              <div>
                <div className="eyebrow">Leadership attention</div>
                <h2 className="card-title">Projects requiring your review</h2>
              </div>
              <div className="row" style={{ gap: 'var(--space-4)', alignItems: 'center' }}>
                <span className="chip outline sm">{scopeLabel}</span>
                <ExplainChartButton
                  question="Explain the project-health rollup I'm looking at: why are projects red or amber, and where should I focus?"
                  buildVisual={() => buildHealthVisual(healthDonutSegments, { note: scopeLabel })}
                />
                <StatusDonutMini
                  segments={healthDonutSegments}
                  size={84}
                  totalLabel="Projects"
                  ariaLabel="Project health distribution"
                />
                <Link className="btn link" to="/projects">
                  All projects →
                </Link>
              </div>
            </header>
            {criticalProjects.length === 0 ? (
              <EmptyState
                title="All projects on track"
                description="No projects currently require leadership intervention."
              />
            ) : (
              <div className="exec-critical-grid">
                {criticalProjects.slice(0, 6)
                  .map((p) => {
                    const tone = bucketProject(p);
                    // Real per-project trend (financial burn). Omitted entirely
                    // when no real series is available — never fabricated.
                    const series = realProjectSeries(p);
                    return (
                      <Link
                        key={p.id}
                        to={`/projects/${p.id}`}
                        className={`exec-critical-card tone-${HEALTH_TONE[tone]}`}
                      >
                        <header className="row spread">
                          <strong>{p.name}</strong>
                          <span className={`chip ${HEALTH_TONE[tone]} sm`}>
                            <span className="chip-dot" /> {formatHealth(p.status, tone)}
                          </span>
                        </header>
                        <div className="muted small">
                          {p.id} · {p.programId || 'Program not assigned'}
                        </div>
                        <div className="exec-critical-meta">
                          <span>schedule {p.healthScores?.schedule ?? '—'}</span>
                          <span>cost {p.healthScores?.cost ?? '—'}</span>
                          <span>risk {p.healthScores?.risk ?? '—'}</span>
                        </div>
                        <div className="exec-critical-spark">
                          {series ? (
                            <Sparkline
                              values={series}
                              width={180}
                              height={28}
                              tone={HEALTH_TONE[tone]}
                              ariaLabel={`${p.name} burn trend`}
                            />
                          ) : null}
                        </div>
                      </Link>
                    );
                  })}
              </div>
            )}
          </div>

          <div className="span-4 card exec-alerts">
            <header className="exec-card-head">
              <div>
                <div className="eyebrow">Predictive alerts</div>
                <h2 className="card-title">Risk Sentinel</h2>
              </div>
              <div className="row execx-zone-scope">
                <span className="chip outline sm">{enterpriseScopeLabel}</span>
                <Link className="btn link" to="/ai/orchestration">
                  All AI runs →
                </Link>
              </div>
            </header>
            {alertList.length === 0 ? (
              <EmptyState
                title="No predictive alerts"
                description="The Risk Sentinel queue is clear."
              />
            ) : (
              <div className="exec-alert-list">
                {alertList.slice(0, 5).map((a) => (
                  <AlertCard
                    key={a.id}
                    severity={
                      a.severity === 'critical' || a.severity === 'high'
                        ? 'red'
                        : a.severity === 'medium'
                          ? 'amber'
                          : 'blue'
                    }
                    title={a.title}
                    body={`${agentLabel(a.agentId) || 'Portfolio Risk agent'} · ${humanizeEnum(
                      a.reviewState,
                      'Pending human review',
                    )}`}
                    chips={
                      a.sourceArtifactIds?.length
                        ? [
                            `${a.sourceArtifactIds.length} linked artifact${
                              a.sourceArtifactIds.length === 1 ? '' : 's'
                            }`,
                          ]
                        : []
                    }
                    actionLabel="View analysis"
                    onAction={() => navigate(`/ai/runs/${a.id}`)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
